import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { PermissionsContext } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  FeatureOnboardingType,
  UpsertUserOnboarding,
  UserOnboarding,
} from "@redotech/redo-model/onboarding/user-onboarding";
import { getLocalStorageJson } from "@redotech/redo-web/utils/local-storage-wrapper";
import { groupMap } from "@redotech/util/map";
import { createEnum } from "@redotech/zod/zod-enum";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDebounce } from "usehooks-ts";
import * as z from "zod/v4";
import {
  FeatureOnboardingDefinition,
  featureOnboardingDefinitions,
} from "./onboarding-types";

const FEATURE_ONBOARDING_CONFIG_KEY = "redo.feature-onboarding-config";
const SESSION_ONBOARDING_SHOWN_KEY = "redo.session-onboarding-shown";

const LEGACY_CROSS_SELL_POPOUT_CONFIG_KEY = "redo.cross-sell-popout-config";

const FeatureOnboardingConfigSchema = z.record(
  createEnum(FeatureOnboardingType),
  z
    .object({
      dismissals: z.number(),
      lastDismissal: z.string().datetime().nullish(),
      completed: z.boolean().optional(),
    })
    .optional(),
);

type FeatureOnboardingConfig = z.infer<typeof FeatureOnboardingConfigSchema>;

const WEEK_IN_HOURS = Temporal.Duration.from({ days: 7 }).total({
  unit: "hours",
});
const DEBOUNCE_MS = 1500;

function getFeatureOnboardingConfigFromLocalStorage(): FeatureOnboardingConfig {
  const config = getLocalStorageJson(FEATURE_ONBOARDING_CONFIG_KEY);
  const parsed = FeatureOnboardingConfigSchema.safeParse(config);
  return parsed.success ? parsed.data : ({} as FeatureOnboardingConfig);
}

function clearFeatureOnboardingConfigFromLocalStorage() {
  localStorage.removeItem(FEATURE_ONBOARDING_CONFIG_KEY);
  localStorage.removeItem(LEGACY_CROSS_SELL_POPOUT_CONFIG_KEY);
}

function hasShownOnboardingThisSession(): boolean {
  return sessionStorage.getItem(SESSION_ONBOARDING_SHOWN_KEY) === "true";
}

function markOnboardingShownInSession() {
  sessionStorage.setItem(SESSION_ONBOARDING_SHOWN_KEY, "true");
}

/**
 * Legacy cross-sell popout config schema for backward compatibility
 */
const LegacyCrossSellPopoutConfigSchema = z.object({
  dismissals: z.number(),
  lastDismissal: z.string().datetime().nullish(),
});

function getLegacyCrossSellConfig() {
  const legacyConfig = getLocalStorageJson(LEGACY_CROSS_SELL_POPOUT_CONFIG_KEY);
  const parsed = LegacyCrossSellPopoutConfigSchema.safeParse(legacyConfig);
  return parsed.success ? parsed.data : null;
}

function userEligibleToSeeOnboarding(
  featureDefinition: FeatureOnboardingDefinition,
  config: UserOnboarding | undefined,
): boolean {
  if (hasShownOnboardingThisSession()) {
    return false;
  }

  // If no config, this is the first time we're considering showing this onboarding
  if (!config) {
    return true;
  }

  if (config.viewHistory.completed) {
    return false;
  }

  const retries = featureDefinition.retries || 0;

  if (config.viewHistory.dismissals > retries) {
    return false;
  }

  const inCooldownSinceLastDismissal =
    config.viewHistory.lastDismissal &&
    Temporal.Instant.compare(
      Temporal.Instant.from(config.viewHistory.lastDismissal),
      Temporal.Now.instant().subtract({ hours: WEEK_IN_HOURS }),
    ) > 0;

  if (inCooldownSinceLastDismissal) {
    return false;
  }

  return true;
}

function getEventsBackendDoesntHave(
  serverEvents: UserOnboarding[],
  localEvents: FeatureOnboardingConfig,
): Omit<UpsertUserOnboarding, "userId">[] {
  const eventsToSync: Omit<UpsertUserOnboarding, "userId">[] = [];

  const legacyCrossSellConfig = getLegacyCrossSellConfig();

  if (legacyCrossSellConfig) {
    // If cross-sell popout is suppressed, remove related events from local config
    const crossSellTypes = [
      FeatureOnboardingType.PACKAGE_PROTECTION_PLUS,
      FeatureOnboardingType.EXPANDED_WARRANTY,
      FeatureOnboardingType.CONVERSION,
    ];
    for (const type of crossSellTypes) {
      if (!localEvents[type]) {
        localEvents[type] = {
          dismissals: legacyCrossSellConfig.dismissals || 0,
          lastDismissal: legacyCrossSellConfig.lastDismissal || null,
          completed: false,
        };
      }
    }
  }

  const localKeys = Object.keys(localEvents) as FeatureOnboardingType[];

  for (const key of localKeys) {
    const localEvent = localEvents[key];
    if (!localEvent) {
      continue;
    }

    const serverEvent = serverEvents.find((event) => event.type === key);

    // If local event exists but server doesn't, we have a mismatch
    if (
      !serverEvent ||
      serverEvent.viewHistory.dismissals < localEvent.dismissals ||
      (localEvent.completed && !serverEvent.viewHistory.completed)
    ) {
      eventsToSync.push({
        type: key,
        viewHistory: {
          dismissals: localEvent.dismissals || 0,
          lastDismissal: localEvent.lastDismissal || null,
          completed: localEvent.completed || false,
        },
      });
    }
  }
  return eventsToSync;
}

export function useFeatureOnboardingManager() {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );

  // Dev helper: call window.__resetOnboarding('cross_border_onboarding') in browser console
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      (window as any).__resetOnboarding = async (type: string) => {
        await platformRpcClient.upsertUserOnboarding({
          type: type as FeatureOnboardingType,
          viewHistory: { dismissals: 0, lastDismissal: null, completed: false },
        });
        sessionStorage.removeItem(SESSION_ONBOARDING_SHOWN_KEY);
        console.debug(`[onboarding] Reset ${type} — reload the page`);
      };
    }
  }, [platformRpcClient]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const userPermissions = useContext(PermissionsContext);
  const { pathname } = useLocation();

  // Load onboarding data from RPC
  const rpcOnboardingData = useLoad(async () => {
    try {
      const serverOnboardingData =
        await platformRpcClient.getUserOnboarding(null);
      const localOnboardingData = getFeatureOnboardingConfigFromLocalStorage();

      const unsyncedEvents = getEventsBackendDoesntHave(
        serverOnboardingData,
        localOnboardingData,
      );
      if (unsyncedEvents.length > 0) {
        for (const event of unsyncedEvents) {
          try {
            await platformRpcClient.upsertUserOnboarding(event);
          } catch (error) {
            console.error("Failed to upsert user onboarding:", error);
          }
        }
        clearFeatureOnboardingConfigFromLocalStorage();
        return await platformRpcClient.getUserOnboarding(null);
      } else {
        clearFeatureOnboardingConfigFromLocalStorage();
        return serverOnboardingData;
      }
    } catch (error) {
      console.error("Failed to load onboarding data from server:", error);
      return [];
    }
  }, [platformRpcClient]);

  const onboardingToShow = useLoad(async () => {
    if (!team || !rpcOnboardingData.value) {
      return undefined;
    }

    const onboardingEvents = rpcOnboardingData.value;

    const groupedEvents = groupMap(onboardingEvents, (event) => [
      event.type,
      event,
    ]);

    const bypassCrossSellCheck = new Set<FeatureOnboardingType>([
      FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED,
      FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED,
      FeatureOnboardingType.NAV_REORGANIZATION,
    ]);

    const sortedDefinitions = Object.values(featureOnboardingDefinitions).sort(
      (a, b) => a.priority - b.priority,
    );

    if (!team.settings.crossSellPopoutEnabled) {
      // When cross-sell is disabled, only show non-cross-sell notifications
      for (const definition of sortedDefinitions) {
        if (!bypassCrossSellCheck.has(definition.type)) {
          continue;
        }
        const userEligible = userEligibleToSeeOnboarding(
          definition,
          groupedEvents.get(definition.type),
        );
        if (!userEligible) {
          continue;
        }
        const teamEligible = await definition.isEligible(
          team,
          user,
          platformRpcClient,
          userPermissions,
        );
        if (teamEligible) {
          return definition.type;
        }
      }
      return FeatureOnboardingType.DISABLED;
    }

    for (const definition of sortedDefinitions) {
      const userEligible = userEligibleToSeeOnboarding(
        definition,
        groupedEvents.get(definition.type),
      );

      if (!userEligible) {
        continue;
      }

      const teamEligible = await definition.isEligible(
        team,
        user,
        platformRpcClient,
        userPermissions,
      );

      if (!teamEligible) {
        continue;
      }

      return definition.type;
    }

    return FeatureOnboardingType.DISABLED;
  }, [team, user, rpcOnboardingData.value, platformRpcClient, pathname]);

  const onboardingType = useDebounce(onboardingToShow.value, DEBOUNCE_MS);

  const handleDismiss = useHandler(async () => {
    if (!onboardingType || onboardingType === FeatureOnboardingType.DISABLED) {
      return;
    }

    setShowOnboarding(false);

    const existingEvent = (rpcOnboardingData.value || []).find(
      (event) => event.type === onboardingType,
    );

    const eventToUpsert = existingEvent
      ? {
          ...existingEvent,
          viewHistory: {
            ...existingEvent.viewHistory,
            dismissals: (existingEvent.viewHistory.dismissals || 0) + 1,
            lastDismissal: Temporal.Now.instant().toString(),
          },
        }
      : {
          type: onboardingType,
          viewHistory: {
            dismissals: 1,
            lastDismissal: Temporal.Now.instant().toString(),
          },
        };

    try {
      await platformRpcClient.upsertUserOnboarding(eventToUpsert);
    } catch (error) {
      console.error("Failed to update onboarding data on server:", error);
    }
  });

  const handleComplete = useHandler(async () => {
    if (!onboardingType || onboardingType === FeatureOnboardingType.DISABLED) {
      return;
    }

    setShowOnboarding(false);

    const existingEvent = (rpcOnboardingData.value || []).find(
      (event) => event.type === onboardingType,
    );

    const eventToUpsert = existingEvent
      ? {
          ...existingEvent,
          viewHistory: { ...existingEvent.viewHistory, completed: true },
        }
      : {
          type: onboardingType,
          viewHistory: { dismissals: 0, lastDismissal: null, completed: true },
        };

    try {
      await platformRpcClient.upsertUserOnboarding(eventToUpsert);
    } catch (error) {
      console.error("Failed to update onboarding data on server:", error);
    }
  });

  useEffect(() => {
    if (onboardingType && onboardingType !== FeatureOnboardingType.DISABLED) {
      setShowOnboarding(true);
      markOnboardingShownInSession();
    }
  }, [onboardingType]);

  return { onboardingType, showOnboarding, handleDismiss, handleComplete };
}
